"use client";

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";

import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { useToast } from "@/app/_module/hooks/use-toast";
import type {
  AgencyTransferJob,
  AgencyTransferMode,
} from "../../services/transfer-agencies-services";

/**
 * Transfer Agencies — progress context. Mirrors ExportProgressContext: a
 * server-persisted job is polled every 2s; the bottom-right widget shows live
 * progress and a per-agency result summary. Survives reloads via the recent-
 * jobs rehydrate on mount.
 */

const TERMINAL: AgencyTransferJob["status"][] = [
  "completed",
  "completed_with_errors",
  "failed",
  "canceled",
];

interface TransferProgressContextValue {
  activeJobs: AgencyTransferJob[];
  startJob: (jobId: string, mode: AgencyTransferMode, totalAgencies: number) => void;
  dismissJob: (jobId: string) => void;
  cancelJob: (jobId: string) => Promise<void>;
}

const TransferProgressContext = createContext<TransferProgressContextValue | undefined>(undefined);

const POLL_INTERVAL_MS = 2_000;
const POLL_MAX_DURATION_MS = 30 * 60 * 1000;
const AUTO_DISMISS_DELAY_MS = 15_000;

export const TransferProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeJobs, setActiveJobs] = useState<AgencyTransferJob[]>([]);
  const { toast } = useToast();

  const pollersRef = useRef<Map<string, { interval: ReturnType<typeof setInterval>; startedAt: number }>>(new Map());
  const dismissTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const stopPolling = useCallback((jobId: string) => {
    const entry = pollersRef.current.get(jobId);
    if (entry) {
      clearInterval(entry.interval);
      pollersRef.current.delete(jobId);
    }
  }, []);

  const scheduleAutoDismiss = useCallback((jobId: string) => {
    const existing = dismissTimersRef.current.get(jobId);
    if (existing) clearTimeout(existing);
    const t = setTimeout(() => {
      setActiveJobs((prev) => prev.filter((j) => j.id !== jobId));
      dismissTimersRef.current.delete(jobId);
    }, AUTO_DISMISS_DELAY_MS);
    dismissTimersRef.current.set(jobId, t);
  }, []);

  const poll = useCallback(async (jobId: string) => {
    try {
      const res = await api.get({
        url: getApiUrl(`/agencies/transfer/jobs/${jobId}`),
        auth: true,
      });
      const job: AgencyTransferJob = (res as any)?.data?.data?.job;
      if (!job) return;

      setActiveJobs((prev) => {
        if (prev.some((j) => j.id === jobId)) {
          return prev.map((j) => (j.id === jobId ? { ...j, ...job } : j));
        }
        return [job, ...prev];
      });

      if (TERMINAL.includes(job.status)) {
        stopPolling(jobId);
        scheduleAutoDismiss(jobId);
      }
    } catch (err: any) {
      if (err?.response?.status === 404) {
        stopPolling(jobId);
        setActiveJobs((prev) => prev.filter((j) => j.id !== jobId));
      }
    }
  }, [scheduleAutoDismiss, stopPolling]);

  const startJob = useCallback((jobId: string, mode: AgencyTransferMode, totalAgencies: number) => {
    if (pollersRef.current.has(jobId)) return;
    setActiveJobs((prev) => {
      if (prev.some((j) => j.id === jobId)) return prev;
      const placeholder: AgencyTransferJob = {
        id: jobId,
        mode,
        status: "queued",
        totalAgencies,
        processedAgencies: 0,
        progressMessage: "Queued…",
        log: [],
      };
      return [placeholder, ...prev];
    });
    const startedAt = Date.now();
    const interval = setInterval(() => {
      if (Date.now() - startedAt > POLL_MAX_DURATION_MS) {
        stopPolling(jobId);
        return;
      }
      poll(jobId);
    }, POLL_INTERVAL_MS);
    pollersRef.current.set(jobId, { interval, startedAt });
    poll(jobId);
  }, [poll, stopPolling]);

  const dismissJob = useCallback((jobId: string) => {
    stopPolling(jobId);
    const t = dismissTimersRef.current.get(jobId);
    if (t) {
      clearTimeout(t);
      dismissTimersRef.current.delete(jobId);
    }
    setActiveJobs((prev) => prev.filter((j) => j.id !== jobId));
  }, [stopPolling]);

  const cancelJob = useCallback(async (jobId: string) => {
    try {
      await api.delete({ url: getApiUrl(`/agencies/transfer/jobs/${jobId}`), auth: true });
      toast({ title: "Transfer canceled" });
    } catch (err: any) {
      toast({ title: "Could not cancel", description: err?.message || "Try again", variant: "destructive" });
    }
  }, [toast]);

  // Rehydrate in-flight jobs on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get({ url: getApiUrl("/agencies/transfer/jobs"), auth: true });
        if (cancelled) return;
        const rows: AgencyTransferJob[] = (res as any)?.data?.data ?? [];
        rows
          .filter((j) => j.status === "queued" || j.status === "processing")
          .forEach((row) => startJob(row.id, row.mode, row.totalAgencies));
      } catch {
        // rehydration is best-effort
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup timers on unmount.
  useEffect(() => {
    return () => {
      pollersRef.current.forEach((p) => clearInterval(p.interval));
      pollersRef.current.clear();
      dismissTimersRef.current.forEach((t) => clearTimeout(t));
      dismissTimersRef.current.clear();
    };
  }, []);

  return (
    <TransferProgressContext.Provider value={{ activeJobs, startJob, dismissJob, cancelJob }}>
      {children}
    </TransferProgressContext.Provider>
  );
};

export const useTransferProgress = (): TransferProgressContextValue => {
  const ctx = useContext(TransferProgressContext);
  if (!ctx) {
    return {
      activeJobs: [],
      startJob: () => {},
      dismissJob: () => {},
      cancelJob: async () => {},
    };
  }
  return ctx;
};
